/**
 * Invoice history (#36679): what an invoice PDF records about itself, stored as S3 user metadata on
 * the PDF when the docs engine saves it, and read back by the staff invoice history. One copy per
 * recipient is saved, so each PDF describes exactly one delivery.
 *
 * S3 user metadata only carries ASCII, so every value is URI-encoded. The fields are display strings,
 * exactly as printed on the invoice.
 */

/** How the invoice went out. */
export type InvoiceChannel = 'scheduled' | 'email' | 'letter' | 'email-letter' | 'generated';

export type InvoiceRecord = {
  channel?: InvoiceChannel;
  // The email address and/or the name and mailing address the invoice went to.
  recipient?: string;
  amountDue?: string;
  dueDate?: string;
  installmentNumber?: string;
  totalInstallments?: string;
  invoiceNumber?: string;
};

const CHANNEL_LABELS: Record<InvoiceChannel, string> = {
  scheduled: 'Scheduled',
  email: 'Email',
  letter: 'Letter',
  'email-letter': 'Email and letter',
  generated: 'Generated'
};

// S3 lower-cases user metadata keys, so these are lower-case already.
const METADATA_KEYS: Record<keyof InvoiceRecord, string> = {
  channel: 'invoice-channel',
  recipient: 'invoice-recipient',
  amountDue: 'invoice-amount-due',
  dueDate: 'invoice-due-date',
  installmentNumber: 'invoice-installment-number',
  totalInstallments: 'invoice-total-installments',
  invoiceNumber: 'invoice-number'
};

export function invoiceChannelLabel(channel: InvoiceChannel | string | undefined | null): string {
  return channel && channel in CHANNEL_LABELS ? CHANNEL_LABELS[channel as InvoiceChannel] : '';
}

export function encodeInvoiceMetadata(record: InvoiceRecord): Record<string, string> {
  const metadata: Record<string, string> = {};
  for (const [field, key] of Object.entries(METADATA_KEYS)) {
    const value = record[field as keyof InvoiceRecord];
    if (value != null && value !== '') {
      metadata[key] = encodeURIComponent(String(value));
    }
  }
  return metadata;
}

export function decodeInvoiceMetadata(metadata: Record<string, string> | undefined | null): InvoiceRecord {
  const record: InvoiceRecord = {};
  for (const [field, key] of Object.entries(METADATA_KEYS)) {
    const raw = metadata?.[key];
    if (raw == null || raw === '') {
      continue;
    }
    let value: string;
    try {
      value = decodeURIComponent(raw);
    } catch {
      value = raw;
    }
    if (field === 'channel' && !(value in CHANNEL_LABELS)) {
      continue;
    }
    (record as Record<string, string>)[field] = value;
  }
  return record;
}
